import { inject, Injectable } from '@angular/core';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { CompaniesService } from '../companies/companies.service';
import { PEOPLE_LIST_DEFAULT_SORT } from '../../constants/people.constants';
import { PeopleService } from '../../../modules/crm/pages/people/people.service';
import { LeadService } from '../../../modules/crm/pages/leads/lead.service';
import { DealService } from '../../../modules/crm/pages/deals/deal.service';
import type { PersonApiDoc } from '../../../modules/crm/pages/people/list/people-list.model';
import type {
    CommandPaletteItem,
    CommandPaletteRecordGroup,
} from './command-palette.types';

const MIN_QUERY_LENGTH = 2;
const RESULT_PAGE_SIZE = 5;

@Injectable({
    providedIn: 'root',
})
export class GlobalCrmSearchService {
    private readonly peopleService = inject(PeopleService);
    private readonly companiesService = inject(CompaniesService);
    private readonly leadService = inject(LeadService);
    private readonly dealService = inject(DealService);

    search(query: string): Observable<CommandPaletteRecordGroup[]> {
        const trimmed = query.trim();
        if (trimmed.length < MIN_QUERY_LENGTH) {
            return of([]);
        }

        return forkJoin([
            this.fetchPeople(trimmed),
            this.fetchCompanies(trimmed),
            this.fetchLeads(trimmed),
            this.fetchDeals(trimmed),
        ]).pipe(map((groups) => groups.filter((group) => group.items.length > 0)));
    }

    private fetchPeople(query: string): Observable<CommandPaletteRecordGroup> {
        return this.peopleService
            .getPeopleList({
                pagination: { page: 1, pageSize: RESULT_PAGE_SIZE },
                sort: PEOPLE_LIST_DEFAULT_SORT,
                filters: { searchQuery: query },
            })
            .pipe(
                map((response) => ({
                    entityType: 'person' as const,
                    label: 'People',
                    items: (response.docs ?? []).map((doc) => this.mapPerson(doc)),
                })),
                catchError((error) => {
                    console.error('Command palette people search failed', error);
                    return of({ entityType: 'person' as const, label: 'People', items: [] });
                }),
            );
    }

    private fetchCompanies(query: string): Observable<CommandPaletteRecordGroup> {
        return this.companiesService
            .getCompanies({
                pagination: { page: 1, pageSize: RESULT_PAGE_SIZE },
                sort: { field: 'createdAt', orderby: 'DESC' },
                filters: {
                    startDate: null,
                    endDate: null,
                    createdBy: null,
                    searchQuery: query,
                    employeeRange: null,
                    industry: null,
                    country: null,
                    scope: 'all',
                },
            })
            .pipe(
                map((response) => {
                    const docs = Array.isArray(response?.items) ? response.items : [];
                    return {
                        entityType: 'company' as const,
                        label: 'Companies',
                        items: docs.map((doc) => this.mapCompany(doc as unknown as Record<string, unknown>)),
                    };
                }),
                catchError((error) => {
                    console.error('Command palette company search failed', error);
                    return of({ entityType: 'company' as const, label: 'Companies', items: [] });
                }),
            );
    }

    private fetchLeads(query: string): Observable<CommandPaletteRecordGroup> {
        return this.leadService
            .getLeads({
                pagination: { page: 1, pageSize: RESULT_PAGE_SIZE },
                sort: { field: 'createdAt', orderby: 'DESC' },
                filters: { searchQuery: query },
            })
            .pipe(
                map((response) => {
                    const docs = Array.isArray(response?.docs) ? response.docs : [];
                    return {
                        entityType: 'lead' as const,
                        label: 'Leads',
                        items: docs.map((doc) => this.mapLead(doc as Record<string, unknown>)),
                    };
                }),
                catchError((error) => {
                    console.error('Command palette lead search failed', error);
                    return of({ entityType: 'lead' as const, label: 'Leads', items: [] });
                }),
            );
    }

    private fetchDeals(query: string): Observable<CommandPaletteRecordGroup> {
        return this.dealService
            .getDeals({
                sort: { field: 'createdAt', orderby: 'DESC' },
                pagination: { page: 1, pageSize: RESULT_PAGE_SIZE },
                searchQuery: query,
                filters: {
                    startDate: '',
                    endDate: '',
                    searchQuery: query,
                    createdBy: '',
                    dealOwner: '',
                    dealStage: [],
                    industry: '',
                    country: '',
                    source: '',
                },
            })
            .pipe(
                map((response) => {
                    const docs = Array.isArray(response?.docs) ? response.docs : [];
                    return {
                        entityType: 'deal' as const,
                        label: 'Deals',
                        items: docs.map((doc) => this.mapDeal(doc)),
                    };
                }),
                catchError((error) => {
                    console.error('Command palette deal search failed', error);
                    return of({ entityType: 'deal' as const, label: 'Deals', items: [] });
                }),
            );
    }

    private mapPerson(doc: PersonApiDoc): CommandPaletteItem {
        const id = String(doc.id ?? '').trim();
        const first = doc.name?.firstName?.trim() ?? '';
        const last = doc.name?.lastName?.trim() ?? '';
        const title = `${first} ${last}`.trim() || 'Unnamed person';
        const subtitle = doc.emails?.primaryEmail?.trim() || doc.jobTitle?.trim() || doc.designation?.trim() || undefined;
        return this.buildRecordItem('person', id, title, subtitle);
    }

    private mapCompany(doc: Record<string, unknown>): CommandPaletteItem {
        const id = String(doc['id'] ?? '').trim();
        const title = String(doc['name'] ?? doc['companyName'] ?? '').trim() || 'Unnamed company';
        const subtitle =
            String(doc['domain'] ?? doc['website'] ?? doc['industry'] ?? '').trim() || undefined;
        return this.buildRecordItem('company', id, title, subtitle);
    }

    private mapLead(doc: Record<string, unknown>): CommandPaletteItem {
        const id = String(doc['id'] ?? '').trim();
        const nameObj = doc['name'] as { firstName?: string; lastName?: string } | undefined;
        const first = nameObj?.firstName?.trim() ?? '';
        const last = nameObj?.lastName?.trim() ?? '';
        const title = `${first} ${last}`.trim() || String(doc['name'] ?? '').trim() || 'Unnamed lead';
        const stageObj = doc['stage'] as { name?: string } | null | undefined;
        const subtitle =
            stageObj?.name?.trim() ||
            String(doc['jobTitle'] ?? doc['companyName'] ?? '').trim() ||
            undefined;
        return this.buildRecordItem('lead', id, title, subtitle);
    }

    private mapDeal(doc: Record<string, unknown>): CommandPaletteItem {
        const id = String(doc['id'] ?? '').trim();
        const title = String(doc['name'] ?? '').trim() || 'Unnamed deal';
        const stageObj = doc['dealStage'] as { name?: string } | string | null | undefined;
        const stageName =
            typeof stageObj === 'string'
                ? stageObj
                : typeof stageObj === 'object' && stageObj
                  ? stageObj.name
                  : undefined;
        const ownerObj = doc['dealOwner'] as { name?: string } | null | undefined;
        const subtitle = stageName?.trim() || ownerObj?.name?.trim() || undefined;
        return this.buildRecordItem('deal', id, title, subtitle);
    }

    private recordRoute(entityType: CommandPaletteRecordGroup['entityType'], id: string): string {
        const segment =
            entityType === 'person'
                ? 'people'
                : entityType === 'company'
                  ? 'companies'
                  : entityType === 'lead'
                    ? 'leads'
                    : 'deals';
        return `/crm/${segment}/${id}/overview`;
    }

    private buildRecordItem(
        entityType: CommandPaletteRecordGroup['entityType'],
        id: string,
        title: string,
        subtitle?: string,
    ): CommandPaletteItem {
        const route = this.recordRoute(entityType, id);
        const keywords = [title, subtitle ?? '', entityType].filter(Boolean);
        return {
            id: `${entityType}-${id}`,
            kind: 'record',
            entityType,
            title,
            subtitle,
            route,
            keywords,
            value: `record:${entityType}:${id} ${title} ${subtitle ?? ''}`.trim(),
        };
    }
}
